
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { ClimateFacility, ClimateRisk, ClimateMitigationStrategy } from '@/types/climate';
import { AlertTriangle, Building, Shield, TrendingUp, DollarSign, Calendar } from 'lucide-react';

export const ClimateRiskDashboard = () => {
  const [facilities, setFacilities] = useState<ClimateFacility[]>([]);
  const [risks, setRisks] = useState<ClimateRisk[]>([]);
  const [strategies, setStrategies] = useState<ClimateMitigationStrategy[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [facilitiesRes, risksRes, strategiesRes] = await Promise.all([
        supabase.from('climate_facilities' as any).select('*'),
        supabase.from('climate_risks' as any).select('*'),
        supabase.from('climate_mitigation_strategies' as any).select('*')
      ]);

      setFacilities((facilitiesRes.data as unknown as ClimateFacility[]) || []);
      setRisks((risksRes.data as unknown as ClimateRisk[]) || []);
      setStrategies((strategiesRes.data as unknown as ClimateMitigationStrategy[]) || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const riskStats = {
    total: risks.length,
    high: risks.filter(r => (r.risk_score || 0) >= 70).length,
    medium: risks.filter(r => (r.risk_score || 0) >= 40 && (r.risk_score || 0) < 70).length,
    low: risks.filter(r => (r.risk_score || 0) < 40).length
  };

  const mitigationStats = {
    planned: strategies.filter(s => s.implementation_status === 'planned').length,
    inProgress: strategies.filter(s => s.implementation_status === 'in_progress').length,
    completed: strategies.filter(s => s.implementation_status === 'completed').length
  };

  const riskByCategory = risks.reduce((acc, risk) => {
    acc[risk.risk_category] = (acc[risk.risk_category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const chartData = Object.entries(riskByCategory).map(([category, count]) => ({
    category: category.replace('_', ' ').toUpperCase(),
    count
  }));

  const pieData = [
    { name: 'High Risk', value: riskStats.high, color: '#ef4444' },
    { name: 'Medium Risk', value: riskStats.medium, color: '#f59e0b' },
    { name: 'Low Risk', value: riskStats.low, color: '#10b981' }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading dashboard...</div>;
  }

  return (
    <div className="grid gap-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Facilities</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{facilities.length}</div>
            <p className="text-xs text-muted-foreground">
              Across all locations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Climate Risks</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{riskStats.total}</div>
            <div className="flex gap-1 mt-1">
              <Badge variant="destructive" className="text-xs">
                {riskStats.high} High
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {riskStats.medium} Medium
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mitigation Strategies</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{strategies.length}</div>
            <p className="text-xs text-muted-foreground">
              {mitigationStats.completed} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Financial Exposure</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${risks.reduce((sum, r) => sum + (r.financial_impact_high || 0), 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Maximum potential impact
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Risk Distribution by Category</CardTitle>
            <CardDescription>Number of identified risks per category</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Risk Severity Distribution</CardTitle>
            <CardDescription>Breakdown of risks by severity level</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest risk assessments and mitigation updates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {risks.slice(0, 5).map((risk) => (
              <div key={risk.id} className="flex items-center justify-between border-b pb-2">
                <div>
                  <p className="font-medium">{risk.risk_name}</p>
                  <p className="text-sm text-muted-foreground">{risk.risk_category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={risk.risk_score && risk.risk_score >= 70 ? 'destructive' : 'secondary'}>
                    Risk Score: {risk.risk_score?.toFixed(0) || 0}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {new Date(risk.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Mitigation Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Mitigation Progress</CardTitle>
          <CardDescription>Current status of mitigation strategies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {strategies.slice(0, 5).map((strategy) => (
              <div key={strategy.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{strategy.strategy_name}</span>
                  <Badge variant="outline">{strategy.implementation_status}</Badge>
                </div>
                <Progress value={strategy.progress_percentage} className="w-full" />
                <p className="text-sm text-muted-foreground">
                  {strategy.progress_percentage}% complete
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
